import { Component, input, output, model, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormGroup } from '@angular/forms';
import { EntityService } from '../../services/entity.service';
import Swal from 'sweetalert2';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
    selector: 'app-entity-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './entity-form.component.html',
    styleUrl: './entity-form.component.scss'
})
export class EntityFormComponent {
    entityForm = input.required<FormGroup>();
    formChange = output<FormGroup>();
    mode = model<FormMode>(FormMode.CREATE);
    formMode = FormMode;
    ccDraft = '';
    ccFocused = false;

    private entityService = inject(EntityService);
    readonly perm = inject(PermissionService);

    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
    });

    ccTags(): string[] {
        return this.parseCc(this.entityForm()?.get('cc')?.value);
    }

    parseCc(raw: unknown): string[] {
        if (Array.isArray(raw)) {
            return raw.map((v) => String(v).trim()).filter(Boolean);
        }
        return String(raw || '')
            .split(/[,;]+/)
            .map((v) => v.trim())
            .filter(Boolean);
    }

    onCcKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
            event.preventDefault();
            this.commitCcDraft();
            return;
        }
        if (event.key === 'Backspace' && !this.ccDraft) {
            const tags = this.ccTags();
            if (tags.length) this.removeCc(tags[tags.length - 1]);
        }
    }

    commitCcDraft(): void {
        const parts = this.parseCc(this.ccDraft);
        this.ccDraft = '';
        if (!parts.length) return;
        const next = [...this.ccTags()];
        for (const part of parts) {
            if (!EMAIL_RE.test(part)) continue;
            if (next.some((t) => t.toLowerCase() === part.toLowerCase())) continue;
            next.push(part);
        }
        this.setCc(next);
    }

    removeCc(tag: string): void {
        this.setCc(this.ccTags().filter((t) => t !== tag));
    }

    private setCc(tags: string[]): void {
        this.entityForm()?.get('cc')?.setValue(tags.join(', '));
    }

    submit() {
        if (this.entityForm() && this.entityForm().valid) {
            this.formChange.emit(this.entityForm());
        }
    }

    editMode() {
        this.mode.set(this.formMode.EDIT);
    }

    desActivate(isActive: boolean, title: string, message: string) {
        const id = this.entityForm().get('id')?.value;
        Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas ${title.toLowerCase()} esta entidad?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Sí, ${title.toLowerCase()}`,
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                this.entityService.update({ id, isActive }).subscribe({
                    next: () => {
                        this.Toast.fire({
                            icon: 'success',
                            title: `La entidad ha sido ${message.toLowerCase()}.`
                        });
                        this.formChange.emit(null);
                    },
                    error: () => Swal.fire('Error', `No se pudo ${message.toLowerCase()} la entidad`, 'error')
                });
            }
        });
    }
}
